import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import Address from "@repo/utils/Address";
import { Get } from "@repo/utils/api";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { api } from "..";

import { Organization, useOrganizations } from "./organizations";

type Branch = {
  id: Branch.Id;
  organizationId: Organization.Id;
  name: string;
} & Address;

declare namespace Branch {
  type Id = `br_${string}`;
}

export type BranchesRest = Get<
  {},
  {
    branches: Branch[];
    organizations: Organization[];
  }
> & {
  [id: Branch.Id]: Get<{}, { branch: Branch; organization: Organization }>;
};

export { type Branch as Branch };

const initialState = {
  byId: {} as Record<Branch.Id, Branch>,
  discoverList: null as Branch.Id[] | null,
};

export const { Context, Provider, useBranches } = FeatureStore.init(
  "Branches",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const [, setOrganizations] = useOrganizations();

    const accessors = {
      byId: attachAction(
        (id: Branch.Id | undefined) => id && store.byId[id],
        (id) => id && actions.fetchSingle(id),
      ),
      discoverList: attachAction(
        () => store.discoverList?.map((id) => store.byId[id]!),
        () => actions.fetchDiscoverList(),
      ),
    };

    const mutations = {
      one(branch: Branch) {
        setStore(
          "byId",
          branch.id,
          reconcile({ ...branch, address: Address.normalize(branch) }),
        );
      },
      many(branches: Branch[]) {
        batch(() => {
          for (const branch of branches) {
            mutations.one(branch);
          }
        });
      },
      discoverList(branches: (Branch | Branch.Id)[]) {
        setStore(
          "discoverList",
          branches.map((branch) =>
            typeof branch === "string" ? branch : branch.id,
          ),
        );
      },
    };

    const actions = withLogger("action", {
      many: mutations.many,
      fetchSingle(id: Branch.Id) {
        return api.branches[id]!.GET({}).then(({ branch, organization }) => {
          setOrganizations.one(organization);
          mutations.one(branch);
        });
      },
      fetchDiscoverList() {
        return api.branches.GET({}).then(({ branches, organizations }) => {
          setOrganizations.many(organizations);
          mutations.many(branches);
          mutations.discoverList(branches);
        });
      },
    });

    return { accessors, actions };
  },
);
